/** SessionStorage clipboard for grocery add form (text + dropdowns only; no images). */

export const GROCERY_FORM_CLIPBOARD_VERSION = 1 as const;

export type GroceryFormClipboardPayload = {
	v: typeof GROCERY_FORM_CLIPBOARD_VERSION;
	fields: Record<string, unknown>;
};

const TRIMMED_CLIPBOARD_FIELDS = [
	'productName',
	'brand',
	'manufacturerName',
	'marketedBy',
	'categoryId',
	'subCategoryId',
	'packType',
	'unitOfMeasure',
	'hsnCode',
	'productCode',
	'fssaiLicenseNo',
	'countryOfOrigin',
];

const NUMERIC_FORM_FIELDS = ['weightage', 'mrp', 'taxRate', 'retailPriceInr'] as const;

const BOOLEAN_DROPDOWN_FIELDS = ['isPerishable', 'isPackaged', 'isHalalCertified'] as const;

const IMAGE_OR_FILE_KEYS = new Set([
	'primaryImage',
	'secondaryImage',
	'productPrimaryImageUrl',
	'productSecondaryImageUrl',
]);

/** Strip server / read-only keys that may appear via initialValues spread from API rows */
const SERVER_KEYS = new Set([
	'id',
	'qualityCheck',
	'createdBy',
	'updatedBy',
	'createdAt',
	'updatedAt',
	'imageUploadedBy',
	'imageUploadedAt',
	'approvedBy',
	'approvedAt',
	'isDeleted',
	'isDraft',
	'tag',
	'category',
	'subCategory',
	'categoryName',
	'subCategoryName',
	'rejectionReason',
	'GroceryRejection',
	'user',
	'updatedByUser',
	'imageUploadedByUser',
	'approvedByUser',
]);

export const getGroceryClipboardStorageKey = (): string => {
	try {
		const companyId = localStorage.getItem('companyId');
		return `medPanel.groceryFormClipboard.${companyId || 'default'}`;
	} catch {
		return 'medPanel.groceryFormClipboard.default';
	}
};

function normalizeLeaf(val: unknown): unknown {
	if (val === null || val === undefined) return val;
	if (typeof val === 'object' && val !== null && 'value' in val && 'label' in val) {
		return (val as { value: unknown }).value;
	}
	if (Array.isArray(val)) {
		return val.map((item) => normalizeLeaf(item));
	}
	return val;
}

/** Build a JSON-safe clipboard payload from Ant Design form values */
export function buildGroceryClipboardPayload(
	values: Record<string, unknown>,
): GroceryFormClipboardPayload {
	const fields: Record<string, unknown> = {};
	for (const [key, v] of Object.entries(values)) {
		if (IMAGE_OR_FILE_KEYS.has(key) || SERVER_KEYS.has(key)) continue;
		fields[key] = normalizeLeaf(v);
	}
	return { v: GROCERY_FORM_CLIPBOARD_VERSION, fields };
}

export function saveGroceryClipboardPayload(payload: GroceryFormClipboardPayload): void {
	sessionStorage.setItem(getGroceryClipboardStorageKey(), JSON.stringify(payload));
}

export function loadGroceryClipboardPayload(): GroceryFormClipboardPayload | null {
	try {
		const raw = sessionStorage.getItem(getGroceryClipboardStorageKey());
		if (!raw) return null;
		const parsed = JSON.parse(raw) as GroceryFormClipboardPayload;
		if (
			!parsed ||
			parsed.v !== GROCERY_FORM_CLIPBOARD_VERSION ||
			typeof parsed.fields !== 'object' ||
			parsed.fields === null
		) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

function normalizeNumericFormField(value: unknown): string | undefined {
	if (value === null || value === undefined) return undefined;
	const normalized =
		typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
	return normalized === '' ? undefined : normalized;
}

export function normalizeClipboardFieldsForPaste(
	fields: Record<string, unknown>,
): Record<string, unknown> {
	const normalizedFields: Record<string, unknown> = { ...fields };

	for (const key of TRIMMED_CLIPBOARD_FIELDS) {
		const value = normalizedFields[key];
		if (typeof value === 'string') {
			normalizedFields[key] = value.trim();
		}
	}

	if (typeof normalizedFields.productName === 'string') {
		normalizedFields.productName = normalizedFields.productName.replace(/\s+/g, ' ');
	}

	for (const key of NUMERIC_FORM_FIELDS) {
		if (key in normalizedFields) {
			normalizedFields[key] = normalizeNumericFormField(normalizedFields[key]);
		}
	}

	for (const key of BOOLEAN_DROPDOWN_FIELDS) {
		const value = normalizedFields[key];
		if (typeof value === 'boolean') {
			normalizedFields[key] = value ? 'true' : 'false';
		}
	}

	return normalizedFields;
}
